import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../services/api';

const RightSidebar = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState({});

  useEffect(() => {
    const fetchSuggested = async () => {
      try {
        const response = await userAPI.getSuggestedUsers();
        const suggestedUsers = response.data || [];
        setConnections(suggestedUsers.slice(0, 5)); // Show max 5 suggestions
      } catch (error) {
        console.error('Error fetching suggested users:', error);
        setConnections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggested();
  }, []);

  const handleFollow = async (userId, username) => {
    try {
      const isCurrentlyFollowing = followingMap[userId];
      
      if (isCurrentlyFollowing) {
        await userAPI.unfollowUser(userId);
        setFollowingMap(prev => ({ ...prev, [userId]: false }));
        toast.success(`Unfollowed @${username}`);
      } else {
        await userAPI.followUser(userId);
        setFollowingMap(prev => ({ ...prev, [userId]: true }));
        toast.success(`Following @${username}`);
      }
    } catch (error) {
      console.error('Error following/unfollowing:', error);
      toast.error('Failed to update follow status');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="right-sidebar-skeleton">
        <Card className="shadow-sm rounded-2xl border-0">
          <CardHeader className="pb-3">
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-start space-x-2.5">
                <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                  <Skeleton className="h-8 w-full rounded-full mt-1" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Suggested Connections */}
      <Card className="shadow-sm hover:shadow-md transition-all rounded-2xl border-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center">
            <UserPlus className="w-4 h-4 mr-2 text-primary" />
            Suggested for You
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {connections.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No suggestions available
            </p>
          ) : (
            <div className="space-y-3">
              {connections.map((connection) => (
                <div key={connection._id} className="flex items-start space-x-2.5">
                  <Link to={`/profile/${connection.username}`}>
                    <Avatar className="w-10 h-10 cursor-pointer hover:scale-105 transition-transform ring-2 ring-purple-100">
                      <AvatarImage src={connection.profileImage} alt={connection.name} />
                      <AvatarFallback className="bg-purple-100 text-purple-700 font-semibold text-xs">
                        {connection.name?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${connection.username}`} className="hover:underline">
                      <p className="font-semibold text-sm text-foreground truncate leading-tight">
                        {connection.name}
                      </p>
                    </Link>
                    <p className="text-xs text-muted-foreground truncate">@{connection.username}</p>
                    <Badge className="mt-1 text-[10px] bg-primary text-white px-2 py-0.5 rounded-full capitalize">
                      {connection.persona}
                    </Badge>
                    {connection.sports?.[0] && (
                      <p className="text-xs text-muted-foreground mt-1">{connection.sports[0]}</p>
                    )}
                    <Button
                      size="sm"
                      variant={followingMap[connection._id] ? 'outline' : 'default'}
                      className={`mt-2 w-full transition-all h-8 text-xs rounded-full font-semibold ${
                        !followingMap[connection._id] 
                          ? 'bg-primary hover:bg-primary/80 text-white' 
                          : 'border-primary text-primary hover:bg-primary/10'
                      }`}
                      onClick={() => handleFollow(connection._id, connection.username)}
                    >
                      {followingMap[connection._id] ? 'Following' : 'Follow'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RightSidebar;
